// client/src/pages/admin/ManageStories.js

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import storyService from '../../services/storyService.js';

export default function ManageStories() {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [storiesRes, catsRes] = await Promise.all([
        storyService.adminGetAll(),
        storyService.getCategories()
      ]);
      setStories(storiesRes.stories || []);
      setCategories(catsRes.categories || []);
    } catch (err) {
      setError('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await storyService.adminDeleteStory(id);
      setStories((prev) => prev.filter((s) => s.storyid !== id));
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete story');
    }
  };

  const filtered = filterCategory
    ? stories.filter((s) => String(s.categoryid) === filterCategory)
    : stories;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-gold mb-1">Manage Stories</h1>
          <p className="text-royal-200 text-base">{stories.length} stories total</p>
        </div>
        <button
          onClick={() => navigate('/admin/stories/new')}
          className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
        >
          + New Story
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-300 text-base mb-4">{error}</div>
      )}

      {/* Filter */}
      <div className="mb-6">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold transition-colors"
        >
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c.categoryid} value={c.categoryid}>{c.categoryname}</option>
          ))}
        </select>
      </div>

      {/* Stories Table */}
      <div className="bg-royal-800/40 border border-royal-600 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-royal-700/50">
              <th className="text-left px-4 py-3 text-base text-royal-200 font-medium">Title</th>
              <th className="text-left px-4 py-3 text-base text-royal-200 font-medium hidden md:table-cell">Category</th>
              <th className="text-left px-4 py-3 text-base text-royal-200 font-medium hidden lg:table-cell">Format</th>
              <th className="text-center px-4 py-3 text-base text-royal-200 font-medium">Status</th>
              <th className="text-right px-4 py-3 text-base text-royal-200 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((story) => (
              <tr key={story.storyid} className="border-b border-royal-800/40 hover:bg-royal-800/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="text-white text-base font-medium">{story.title}</div>
                  {story.author && <div className="text-royal-200 text-sm italic">by {story.author}</div>}
                  {story.subcategoryname && (
                    <div className="text-royal-200 text-sm mt-0.5">{story.subcategoryname}</div>
                  )}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className="text-royal-200 text-sm">{story.categoryname}</span>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <span className={`text-sm px-2 py-0.5 rounded ${
                    story.contentformat === 'html'
                      ? 'bg-gold/10 text-gold'
                      : 'bg-royal-800 text-royal-200'
                  }`}>
                    {story.contentformat || 'text'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-sm px-2 py-0.5 rounded ${
                    story.ispublished
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-royal-800 text-royal-200'
                  }`}>
                    {story.ispublished ? 'Published' : 'Draft'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => navigate(`/admin/stories/edit/${story.storyid}`)}
                      className="text-gold hover:text-gold-light text-sm transition-colors"
                    >
                      Edit
                    </button>
                    {deleteConfirm === story.storyid ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(story.storyid)}
                          className="text-red-400 hover:text-red-300 text-sm transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-royal-200 hover:text-white text-sm transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(story.storyid)}
                        className="text-red-400 hover:text-red-300 text-sm transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-royal-200 text-base">
                  No stories found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}